<template>
  <div v-if="stock" class="mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <span style="font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.85);">
        <i class="bi bi-box-seam text-primary mr-1"></i> Stock
        <span class="text-secondary" style="font-size:12px;font-weight:400;">right now</span>
      </span>
      <a href="/inventory" class="text-primary" style="font-size:12px;font-weight:600;">
        Open inventory <i class="bi bi-chevron-right" style="font-size:10px;"></i>
      </a>
    </div>

    <div class="row" style="row-gap:20px;">
      <div class="col-12 col-xl-5">
        <div class="bg-white border h-100" :style="card">
          <div class="text-secondary mb-1" style="font-size:13px;">
            Stock value (at buying price)
            <i class="bi bi-question-circle" style="font-size:11px;" title="Units in stock × buying price - the same figure as the Inventory page. Rooms and services are not stock."></i>
          </div>
          <div class="text-dark mb-3" style="font-size:28px;font-weight:700;letter-spacing:-0.5px;">{{ money(stock.cost_value) }}</div>

          <div class="row" style="row-gap:16px;">
            <div v-for="s in stats" :key="s.label" class="col-6">
              <div class="text-secondary mb-1" style="font-size:12px;">
                {{ s.label }}
                <i v-if="s.help" class="bi bi-question-circle" style="font-size:11px;" :title="s.help"></i>
              </div>
              <span :class="s.danger ? 'text-danger' : 'text-dark'" style="font-size:17px;font-weight:700;">{{ s.value }}</span>
            </div>
          </div>

          <div v-if="stock.negative || stock.no_cost" class="mt-3 pt-3 border-top" style="font-size:12px;">
            <div v-if="stock.negative" class="text-danger"><i class="bi bi-exclamation-triangle mr-1"></i>{{ stock.negative }} item(s) below zero - sold before being received. Not counted in the value.</div>
            <div v-if="stock.no_cost" class="text-danger mt-1"><i class="bi bi-exclamation-triangle mr-1"></i>{{ stock.no_cost }} item(s) in stock have no buying price, so the value is understated.</div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-6 col-xl-4">
        <div class="bg-white border h-100" :style="card">
          <div class="mb-3" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Most money tied up</div>
          <a v-for="p in stock.top_value" :key="p.id" :href="itemLink(p.id)" class="d-flex justify-content-between align-items-center py-2 border-bottom text-decoration-none" style="font-size:13px;gap:10px;">
            <span class="text-dark text-truncate">{{ p.name }} <span class="text-secondary">· {{ qty(p.units) }} {{ p.unit || '' }}</span></span>
            <strong class="text-dark text-nowrap">{{ money(p.value) }}</strong>
          </a>
          <p v-if="!stock.top_value.length" class="text-secondary mb-0" style="font-size:12px;">Nothing in stock.</p>
        </div>
      </div>

      <div class="col-12 col-md-6 col-xl-3">
        <div class="bg-white border h-100" :style="card">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <span style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;color:rgba(253,71,25,0.8);">Running low</span>
            <span class="badge badge-dark">{{ stock.low_stock_count }}</span>
          </div>
          <a v-for="p in stock.low_stock" :key="p.id" :href="itemLink(p.id)" class="d-flex justify-content-between align-items-center py-2 border-bottom text-decoration-none" style="font-size:13px;gap:10px;">
            <span class="text-dark text-truncate">{{ p.name }}</span>
            <span class="text-danger text-nowrap">{{ qty(p.units) }} / {{ qty(p.minimum) }}</span>
          </a>
          <p v-if="!stock.low_stock.length" class="text-secondary mb-0" style="font-size:12px;">Nothing below its minimum.</p>
          <a v-if="stock.low_stock_count" href="/bulk-restock" class="btn btn-outline-primary btn-block btn-sm mt-3">Restock</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/** Stock block on /analytics/beta - the parent fetches /api/sales-analytics/stock (403 = hidden). */
export default {
  name: 'StockBlock',

  props: {
    stock: { type: Object, default: null },
  },

  data() {
    return { card: 'border-radius:14px;border-color:#e9ecef !important;padding:20px;' };
  },

  computed: {
    stats() {
      const s = this.stock;
      const out = [
        { label: 'Selling value', value: this.money(s.retail_value), help: 'Units × selling price, for items that have a selling price.' },
        { label: 'Profit on the shelf', value: this.money(s.potential_profit), help: 'What you would make selling everything on hand at today\'s prices (items with a selling price).' },
        { label: 'Items in stock', value: s.in_stock.toLocaleString('en-US') },
        { label: 'Out of stock', value: s.out_of_stock.toLocaleString('en-US'), danger: s.out_of_stock > 0 },
        { label: 'Not selling', value: s.dead.count ? `${s.dead.count} · ${this.money(s.dead.value)}` : '0', danger: s.dead.count > 0, help: `Items for sale with stock that haven't sold in ${s.dead.after_days} days.` },
      ];
      if (s.materials_value > 0) {
        out.push({ label: 'Raw materials', value: this.money(s.materials_value), help: 'Stock with no selling price (ingredients, materials). Included in the stock value.' });
      }
      return out;
    },
  },

  methods: {
    itemLink(id) { return `/inventory-activity?p-identifier-id=${id}`; },
    qty(v) { return Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 }); },
    money(v) { return 'KES ' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 }); },
  },
};
</script>
